'use client';

import { bunja, createScope } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';

import { Styles, css } from '@flipster/styled-system/css';
import { convertDate } from '@flipster/utils/datetime';

import { useMounted } from '../misc/lifecycle/mount';
import { globalTimerBunja } from '../misc/timer/timer';

const units = ['MM', 'dd', 'hh', 'mm', 'ss'] as const;
type UnitType = (typeof units)[number];

const second = 1000;
const minute = second * 60;
const hour = minute * 60;
const day = hour * 24;
const month = day * 30;

const intervalUnitMapper: { [key in UnitType]: number } = {
  MM: month,
  dd: day,
  hh: hour,
  mm: minute,
  ss: second,
} as const;

type CountdownResult = Partial<Record<UnitType, number>>;

const CountdownTargetDateScope = createScope<Date | string>();
const CountdownMinUnitScope = createScope<UnitType>();
const CountdownMaxUnitScope = createScope<UnitType>();

const countdownBunja = bunja(() => {
  const { nowPerSecondAtom } = bunja.use(globalTimerBunja);
  const targetDateOrigin = bunja.use(CountdownTargetDateScope);
  const minUnit = bunja.use(CountdownMinUnitScope);
  const maxUnit = bunja.use(CountdownMaxUnitScope);

  const countdownAtom = atom((get) => {
    const nowPerSecond = get(nowPerSecondAtom);
    const targetDate = convertDate(targetDateOrigin);
    if (!targetDate) return null;

    let difference = targetDate.getTime() - nowPerSecond;
    if (difference < 0) return null;

    const maxUnitIndex = units.indexOf(maxUnit);
    const minUnitIndex = units.indexOf(minUnit);
    if (maxUnitIndex === -1 || minUnitIndex === -1 || maxUnitIndex > minUnitIndex) {
      return null;
    }

    const result: CountdownResult = {};
    for (let i = maxUnitIndex; i <= minUnitIndex; i++) {
      const unit = units[i];
      result[unit] = Math.floor(difference / intervalUnitMapper[unit]);
      difference %= intervalUnitMapper[unit];
    }
    return result;
  });

  const onEndedAtom = atom((get) => get(countdownAtom) == null);

  return { countdownAtom, onEndedAtom };
});

interface UseCountdownDateProps {
  targetDate: Date | string;
  minUnit?: UnitType;
  maxUnit?: UnitType;
  onEnd?: () => void;
}

/**
 * 카운트다운 타이머를 관리하는 Hook
 * @param props.targetDate - 목표 날짜
 * @param props.minUnit - 최소 표시 단위 (기본값: 'ss')
 * @param props.maxUnit - 최대 표시 단위 (기본값: 'hh')
 * @param props.onEnd - 카운트다운 종료 시 호출될 콜백 (targetDate마다 한 번만 호출됨)
 * @returns 카운트다운 데이터 객체 또는 null (종료된 경우)
 */
export const useCountdownDate = ({ targetDate, minUnit = 'ss', maxUnit = 'hh', onEnd }: UseCountdownDateProps) => {
  const { countdownAtom, onEndedAtom } = useBunja(countdownBunja, [
    CountdownTargetDateScope.bind(targetDate),
    CountdownMinUnitScope.bind(minUnit),
    CountdownMaxUnitScope.bind(maxUnit),
  ]);
  const countdown = useAtomValue(countdownAtom);
  const onEnded = useAtomValue(onEndedAtom);

  // TODO use-effect-event 로 넘어가야함
  useEffect(() => {
    if (onEnded) onEnd?.();
    // oxlint-disable-next-line exhaustive-deps
  }, [onEnded]);

  return countdown;
};

/**
 * format 문자열을 파싱하여 렌더링 정보를 추출하는 함수
 * @param format - 카운트다운 포맷 문자열 (예: '${hh}:${mm}:${ss}' 또는 '${dd}일 ${hh}시 ${mm}분 ${ss}초')
 * @returns maxUnit - 사용된 최대 시간 단위
 * @returns minUnit - 사용된 최소 시간 단위
 * @returns parts - 렌더링할 파트 배열 (숫자 또는 텍스트)
 */
const parseFormat = (format: string) => {
  const usedUnits: UnitType[] = [];
  const parts: Array<{ type: 'unit' | 'text'; unit?: UnitType; text?: string }> = [];

  let currentIndex = 0;
  let textBuffer = '';

  while (currentIndex < format.length) {
    if (format.slice(currentIndex, currentIndex + 2) === '${') {
      if (textBuffer) {
        parts.push({ type: 'text', text: textBuffer });
        textBuffer = '';
      }
      const closeIndex = format.indexOf('}', currentIndex);
      if (closeIndex !== -1) {
        const unitKey = format.slice(currentIndex + 2, closeIndex) as UnitType;
        if (units.includes(unitKey)) {
          usedUnits.push(unitKey);
          parts.push({ type: 'unit', unit: unitKey });
        }
        currentIndex = closeIndex + 1;
        continue;
      }
    }
    textBuffer += format[currentIndex];
    currentIndex++;
  }

  if (textBuffer) {
    parts.push({ type: 'text', text: textBuffer });
  }

  const sortedUnits = [...usedUnits].sort((a, b) => units.indexOf(a) - units.indexOf(b));
  const maxUnit = sortedUnits[0] || 'hh';
  const minUnit = sortedUnits[sortedUnits.length - 1] || 'ss';

  return { maxUnit, minUnit, parts };
};

interface CountdownDateProps {
  targetDate: Date | string;
  format?: string;
  overText?: string;
  onEnd?: () => void;
  css?: Styles;
}

/**
 * @param props.targetDate - 목표 날짜
 * @param props.format - 카운트다운 포맷 문자열 (기본값: '${hh}:${mm}:${ss}')
 * @param props.overText - 카운트다운 종료 시 표시할 텍스트
 * @param props.onEnd - 카운트다운 종료 시 호출될 콜백 (targetDate마다 한 번만 호출됨)
 * @param props.css - 전체 컨테이너에 적용될 스타일
 */
export const CountdownDate = ({
  targetDate,
  format = '${hh}:${mm}:${ss}',
  overText = '',
  onEnd,
  css: cssProps,
}: CountdownDateProps) => {
  const isMounted = useMounted();

  const { maxUnit, minUnit, parts } = useMemo(() => parseFormat(format), [format]);

  const countdownData = useCountdownDate({
    targetDate,
    minUnit,
    maxUnit,
    onEnd,
  });

  const className = css({ wordBreak: 'keep-all' }, cssProps);

  if (!isMounted) return <p className={className} translate='no' />;

  if (countdownData === null && overText) {
    return (
      <p className={className} translate='no'>
        {overText}
      </p>
    );
  }

  return (
    <p className={className} translate='no'>
      {parts.map((part, index) => {
        if (part.type === 'unit' && part.unit && countdownData) {
          const value = countdownData[part.unit] ?? 0;
          return <span key={`${part.unit}-${index}`}>{String(value).padStart(2, '0')}</span>;
        }
        if (part.type === 'text') {
          return <span key={`text-${index}`}>{part.text}</span>;
        }
        return null;
      })}
    </p>
  );
};
